/* Created by Lou Mauget on 2017-11-06 */

import $ from 'jquery';
import _ from 'lodash';
import {View} from 'backbone';
import {Model} from 'backbone';
import viewTemplate      from 'dashboard/forms/permission-details-view.ejs';
import util from 'util/Util';
import constants from 'Constants';
import ConfirmationModal from 'dashboard/forms/modal/ConfirmationModal';

//==============================================================================================================
// A viewer permissions panel BackBone view component. It shares the parent FormDetailsView model.
// Mainly responsible for painting the viewTemplate into an element chosen by the parent view.
// Also works with ReportDetailsView.
//
// Special notes:
// Selecting "Full Control" sets all the perms above it to that column type's allow or deny while disabling them
// Selecting "World" resets all checkboxes below it in both columns while disabling them.
//
// Lou Mauget 2017-11-07
//==============================================================================================================

class PermissionsDetailView extends View {
    initialize(opts) {

        this.options = opts || {};
        this.template = viewTemplate;

        this.normalizePerms(this.model.get('currentPermissionList'), this.model.get("isReport"));

        this.events = {
            'change input[type="checkbox"]' : 'managePermissions'
        };

        this.listenTo(this.model, 'change', this.render);

        this.isReport = this.model.get("isReport");

        return this;
    }


    //===================================================================================================================
    // Permissions set by the Flash app can be sparse arrays of 0-6 rows, each having a standard permission type
    // Here, we ensure that all permissions have six ordered rows, each with a standard type at a well-known ordinal.
    // Model currentPositionList assumed passed as the parameter.
    //
    // Note that internal permission names differ from their names on the UI, in two cases: report (Data), and
    // administer (Full control.
    //
    // Further note that the positions of api and administer are interchanged from their row positions on the form
    // details UI.
    //
    // In normalizePerms, we force all incoming permissions into a fixed-size array (except for 'world, which is always
    // one special row).
    //
    // We do not remove unused rows upon save. The legacy app handles rows having en empty #text attribute without issue.
    // Permissions are a small payload. If the extra sent data is seen as an issue, we could de-gas the
    // permissions before persisting them (we don't do this).
    //===================================================================================================================
    normalizePerms( perms, isReport=false ) {

        // Index the standardized Permissions array items by @type;
        // This establishes a standard ordering of rows by type, thus simpliying the UI.
        // We compute this index during view intialtization instead of hand-coding it -
        // - cuts down on human mistakes (plus this human is lazy).
        const type2rowIndex = ( permVals => {
            const idx = {};
            let key = 0;
            permVals.forEach( v => {
                const _type = v['@type'];
                idx[_type] = key++;
            });
            return idx;
        })(isReport ? PermissionsDetailView.protoReportPermssion : PermissionsDetailView.protoPermssion);

        perms.forEach( pp => {
            // Expand Permissions to six standard rows:
            const newPermission = _.cloneDeep( isReport
                ? PermissionsDetailView.protoReportPermssion
                : PermissionsDetailView.protoPermssion );
            util.enforceArrayType(pp.Permission).forEach(p => {
                newPermission[type2rowIndex[p['@type']]]['#text'] = p['#text'];
            });

            // -- Replace inbound Permissions array with expanded normalized rendition.
            pp.Permission = newPermission;

        });
    }


    // Disables all UI rows but the row having ID prefix 4 (form) or 3 (report) -- the administer / full-control item
    static disableNormalRows(wantDisabled, isReport=false)  {

        const fullControlIdPrefix = isReport
            ? constants.REPORTS_FULL_CONTROL_PERM_DATA_INDEX
            : constants.FORMS_FULL_CONTROL_PERM_DATA_INDEX;

        const limit = isReport
            ? constants.REPORTS_PERMISSION_ITEM_COUNT
            : constants.FORMS_PERMISSION_ITEM_COUNT;

        for (let r = 0; r < limit; r++) {
            if (wantDisabled && r !== fullControlIdPrefix) {
                $(`#${r}allow`).prop('disabled', 'disabled');
                $(`#${r}deny`).prop('disabled', 'disabled');
            } else {
                $(`#${r}allow`).prop('disabled', false);
                $(`#${r}deny`).prop('disabled', false);
            }
        }
    }

    // Disables all but row 0 "allow" and "deny" if viewer is "world"
    static conditionWorldPermUI(selectedViewer, rowCount) {
        if ('world' === selectedViewer.toLowerCase()) {

            $(`#${0}allow`).prop('disabled', false);
            $(`#${0}deny`).prop('disabled', false);

            for (let r = 1; r < rowCount; r++) {
                $(`#${r}allow`).prop('disabled', 'disabled');
                $(`#${r}deny`).prop('disabled', 'disabled');
            }
        }
    }

    static clearPermPanel( rowCount ){
        for (let r = 0; r < rowCount; r++) {
            $(`#${r}allow`).prop( {'checked': false, 'disabled': 'disabled' });
            $(`#${r}deny`).prop( {'checked': false, 'disabled': 'disabled' });
        }
    }


    static selectedViewerPerms(selectedUserId, selectedUser, permissionList)  {
        let perms = [];
        if (permissionList) {
            permissionList.forEach(item => {
                if (item['@id'] === selectedUserId && item['@caption'] === selectedUser) {
                    perms = item.Permission;
                }
            });
        } else {
            console.log(`selectedViewerPerms passed list:`, permissionList);
        }
        return perms;
    }


    static selectPermissionViewer(userId, userName) {
        window.setTimeout(() =>  $(`select[name='permissions'] option[id='${userId}'] option[value='${userName}']`)
            .attr('selected', 'selected').trigger('change'), 0);
    }


    //================================================================================================================
    // This function-of-functions is the main instance method of this class. It is has a report vs forms mode,
    // as dictated by model 'isReport" boolean.
    //================================================================================================================
    managePermissions(ev) {
        ev.preventDefault();

        // Maintainers: uncomment following to log what's input to a checkbox click:
        /*console.log(`managePermissions`, ev);
        console.log(`id ${ev.target.id}`);
        console.log(`row ${parseInt(ev.target.id)}`);
        console.log(ev.target.id.includes('allow') ? 'allow' : 'deny');
        console.log(`value ${ev.target.value}`);
        console.log(`checked ${ev.target.checked}`);*/

        // Permissions are rows, each of two columns.
        // + Each row corresponds to a user that represents a "person or group or position" user view role.
        // + A row's first column is "allow"; its second column is "deny".
        // + Form permission index 4 (3 if reportif) is special, overriding all other rows. Its columns
        //     may both be unset, but, otherwise, only one or neither may be set
        // + A set permission row 4 (3 for report) checkbox replicates its setting to all normal rows, toggling
        //     the opposite itme of all normal rows. Normal rows become disabled.
        //
        // Note that permission row 4 is named "administer" internally, but it renders as the final UI panel row
        // named "Full control".
        //
        // Resetting row 4 to neither checkbox set, causes all normal items of that column to be set and all normal
        // items of the other column to be unset.
        const
            fullControlModelIndex = this.isReport
                ? constants.REPORTS_FULL_CONTROL_PERM_DATA_INDEX
                : constants.FORMS_FULL_CONTROL_PERM_DATA_INDEX;
        const
            maxRows = this.isReport
                ? constants.REPORTS_PERMISSION_ITEM_COUNT
                : constants.FORMS_PERMISSION_ITEM_COUNT;
        const
            column = ev.target.id.includes('allow') ? 'allow' : 'deny',
            modelRow = parseInt(ev.target.id),
            isChecked = ev.target.checked;

        const fnSetColumn = ( (colName, boolVal) => {
            const oppositeCol = colName === 'allow' ? 'deny' : 'allow';
            for (let r = 0; r < maxRows; r++) {
                if (r !== fullControlModelIndex) {
                    $(`#${r}${colName}`).prop('checked', boolVal);
                    $(`#${r}${oppositeCol}`).prop('checked', !boolVal);
                }
            }
        });

        // Bypass BB event mechanism to enable apply change button
        const fnEnableApplyChange = ( model => {
            model.set( 'hasChanges', true, {silent: true});
            if (model.get('isReport')) {
                $('#reportActionSection').addClass('hide');
                $('#reportPermissionSection').removeClass('hide');
            } else {
                $('#formActionSection').addClass('hide');
                $('#formPermissionSection').removeClass('hide');
            }
        });

        // Bypass BB events to reflect permissions panel UI detail for selectedViewer to model currentPermissionList for selectedViewer.
        // Why? More control of implicit states peculair to permissions
        const fnHarvestUIPerms = ( model => {

            const
                selectedViewer = model.get('selectedViewer'),
                selectedViewerId = model.get('selectedViewerId'),
                permsList = model.get('currentPermissionList'),
                perms = util.enforceArrayType(PermissionsDetailView.selectedViewerPerms(selectedViewerId, selectedViewer, permsList));

            if (perms && perms[0]) {
                // Enumerates model rows: r is a model item index
                perms.forEach( (v, r) => {

                    const
                        isAllow = $(`#${r}allow`).prop('checked'),
                        isDeny = $(`#${r}deny`).prop('checked');

                    // Note third state: "neither allow nor deny", if user clicks a set checkbox paired to unset checkbox
                    v['#text'] = isAllow ? 'allow' : (isDeny ? 'deny' : '');

                });

                // UI and model in-sync already. Enable the Apply Changes button directly
                fnEnableApplyChange(model);

            }
            // Maintain selection in user/viewer listbox
            PermissionsDetailView.selectPermissionViewer( selectedViewerId, selectedViewer );
        });

        // Method entry point:

        // Set the opposite column checked state to the flipped state of current column, unless row is fullControlModelIndex.
        // Note that fullControlModelIndex is not the final row in the model. It IS the final row in the UI, however.
        // Rule 1: click a box ==> toggles that box; clears opposite box (result is  one box set or no boxes set -- alternating on successive clicks
        if (modelRow !== fullControlModelIndex) {
            const oppositeId = `#${modelRow}${column === 'allow' ? 'deny' : 'allow'}`;
            $(oppositeId).prop('checked', false);
            // console.log(`#${ev.target.id} ==> ${isChecked}; ${oppositeId} => false`);
        } else {

            // Full control means force-replace setting of all earlier checkboxes to the full control allow/deny column value.
            // i.e. when a full control box becomes checked, we replicate full control checkboxes to "normal" rows; disabling those checkboxes on both columns.
            // It IS legal to have both full control checkboxes unset; this means to enable both columns of all "normal" rows.
            // When a full control checkbox becomes unchecked, then ewe enable all earlier checkboxes.

            // Replicate full control set state to "normal" rows. Don't if unset.
            if (isChecked) {
                fnSetColumn(column, isChecked);
            }

            if (isChecked) {
                const oppositeId = `#${fullControlModelIndex}${column === 'allow' ? 'deny' : 'allow'}`;
                $(oppositeId).prop( 'checked', !isChecked );
            }

            // At this point fullControlRow column is checked, at most, or no fullControlRow columns are checked.
            // If either is checked, we disable normal rows' checkboxes , else enable all normal row 'allow'  checkboxes.

            const isEitherFullControlChecked = $(`#${fullControlModelIndex}allow`).prop('checked') || $(`#${fullControlModelIndex}deny`).prop('checked');
            // console.log(`isEitherFullControlChecked`, isEitherFullControlChecked)

            PermissionsDetailView.disableNormalRows( isEitherFullControlChecked, this.model.get('isReport') );

            if (!isEitherFullControlChecked) {
                fnSetColumn( column, true);
                //enable both columns of normal (non-admin/full-control) rows
                PermissionsDetailView.disableNormalRows(false, this.model.get('isReport'));
            }

        }
        // window.setTimeout(() => fnHarvestUIPerms( this.model ), 0);
        fnHarvestUIPerms( this.model );

    }

    static get protoPermssion() {
        return [
            {'@type': 'view', '#text': ''},
            {'@type': 'edit', '#text': ''},
            {'@type': 'deploy', '#text': ''},
            {'@type': 'report', '#text': ''},
            {'@type': 'administer', '#text': ''},
            {'@type': 'api', '#text': ''}
        ]
    }


    static get protoReportPermssion() {
        return [
            {'@type': 'view', '#text': ''},
            {'@type': 'edit', '#text': ''},
            {'@type': 'deploy', '#text': ''},
            {'@type': 'administer', '#text': ''}
        ]
    }

    static getProtoPermissionParent(item, isReport = false) {
        return _.cloneDeep(
            {
                '@id': item.items['@id'],
                '@type': item.items['@type'],
                '@caption': item.items['@caption'],
                'Permission': isReport
                    ?   PermissionsDetailView.protoReportPermssion
                    : PermissionsDetailView.protoPermssion
            });
    }

    /*====================================================================================*/
    /* Item may be a user, group, or position
    /*====================================================================================*/
    static createPermissionForItem(item, isReport=false ){
        // console.log(`PermissionBoilerplate input`, item);
        const itemProtype = PermissionsDetailView.getProtoPermissionParent( item, isReport );
        if ('World' === itemProtype['@type']) {
            itemProtype['@id'] = '-2147483524';
            itemProtype.Permission = [ {'@type': 'view', '#text': ''} ];
        }
        return itemProtype;
    }

    static createUserSpecialItem(name){
        return {'@type': `${name.toLowerCase()}`, '@id': '-2147483524', '@caption': `${name}`};
    }

    //=================================================================================================================
    // Validate that permissions are not completely empty. If empty, pop a dialog confirming if the user wants
    // to proceed. If so, attmept to proceed. Otherwise do nothing.
    //
    // Signature:  (isPrivateBool, current permissions, function that handles applying permssion changes, the "this" pointer).
    //=================================================================================================================
    static validateChanges( isPrivate, permissions, actionFn, context){

        // IEF tests for any permission parent having an all-nil Permissions array with all #text entries empty.
        const isValid = ( users => {
            let hasEmpties    = 0;
            util.enforceArrayType(users).forEach( u => {
                let specifiedCount = 0;
                util.enforceArrayType(u.Permission).forEach( p => {
                    if (p && p['#text']){
                        specifiedCount++;
                    }
                });
                hasEmpties = hasEmpties || !specifiedCount
            });
            return !hasEmpties;
        })(permissions);

        // Force "allow view" if form private and !isValid -- we make it valid by granting view-only permission
        // Force "allow" on first four capabilities if user is "Everyone"
        if (!isValid && isPrivate) {
            const users = permissions;
            util.enforceArrayType(users).forEach( u => {
                let specifiedCount = 0;
                const up = util.enforceArrayType(u.Permission);
                up.forEach( p => {
                    if (p && p['#text']){
                        specifiedCount++;
                    }
                });
                if (!specifiedCount && up && up.length && up[0]) {
                    if ('Everyone' === u['@caption']) {
                        let cnt = 0
                        up.forEach(p => {
                            if (cnt < 4) {
                                p['#text'] = 'allow';
                            }
                            cnt++;
                        });
                    } else {
                        up[0]['#text'] = 'allow';
                    }
                }
            });
        }

        // console.log(`isPrivate ${isPrivate}`);
        // console.log('permissions', permissions);


        if (isPrivate || isValid) {
            actionFn.apply(context);
        } else {
            const
                confirmationTitle = 'Permission Warning',
                confirmationText =
                    '<div class="row">' +
                    '  <div class="col-sm-1"><i class="pf-warning"></i></div>' +
                    '  <div class="col-sm-11">' +
                    '    <p> At least one of your users, positions or user-groups has no permission selected. ' +
                    'Applying changes will remove that role.</p>' +
                    '    <p>Are you sure you want to apply changes?</p>' +
                    '  </div>' +
                    '</div>';

            new ConfirmationModal( {
                model: new Model( {
                    confirmationTitle: confirmationTitle,
                    confirmationText: confirmationText,
                    height: 220,
                    width: 500
                })
            }).render().then(result => {
                if ('Yes' === result) {
                    actionFn.apply(context);
                }
                // console.log(result);
            });
        }
    }


    render() {
        this.$el.empty();
        if(this.template) {
            this.$el.html(this.template(this.model.toJSON()));
        }

        this.delegateEvents();
        return this;
    }

}

export default PermissionsDetailView;
